import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type QuienEntraHoy = components['schemas']['QuienEntraHoyRead']
export type FilaQuienEntra = components['schemas']['FilaQuienEntraRead']
export type PreviewIngreso = components['schemas']['PreviewRead']
export type ChequeoIngreso = components['schemas']['ChequeoRead']
export type FilaCongelada = components['schemas']['FilaCongeladaRead']
export type DecisionIngreso = components['schemas']['DecisionIngresoRead']

export const QUIEN_ENTRA_PAGE_SIZE = 20

function obras(businessId: string | undefined) {
  return `/businesses/${businessId}/proyectos`
}

function decisiones(businessId: string | undefined) {
  return `/businesses/${businessId}/decisiones-ingreso`
}

/** `'todos'` es la ausencia del parámetro y no viaja al servidor. */
export type EstadoQuienEntra = 'todos' | 'no_habilitados' | 'sin_verificar' | 'habilitados'

/** Sin `sortBy`: el orden es una prioridad calculada por el servidor. `estado` filtra en el servidor (R6/U19)
 * para que el total del pie sea el de la lista. */
export function useQuienEntraHoy(
  businessId: string | undefined,
  proyectoId: string | undefined,
  filtros: {contratista?: string; cargo?: string; estado?: EstadoQuienEntra; page: number}
) {
  return useQuery({
    queryKey: ['quien-entra-hoy', businessId, proyectoId, filtros],
    queryFn: async () =>
      (
        await api.get<QuienEntraHoy>(`${obras(businessId)}/${proyectoId}/quien-entra-hoy`, {
          params: {
            contratista: filtros.contratista || undefined,
            cargo: filtros.cargo || undefined,
            estado: filtros.estado && filtros.estado !== 'todos' ? filtros.estado : undefined,
            page: filtros.page,
            page_size: QUIEN_ENTRA_PAGE_SIZE,
          },
        })
      ).data,
    enabled: !!businessId && !!proyectoId,
    placeholderData: (previous) => previous,
  })
}

/** Fresco en cada apertura: el acuse que exige el POST describe la hoja vista, y uno cacheado firmaría una foto vieja. */
export function usePreviewIngreso(
  businessId: string | undefined,
  employeeId: string | undefined,
  proyectoId: string | undefined
) {
  return useQuery({
    queryKey: ['ingreso-preview', businessId, employeeId, proyectoId],
    queryFn: async () =>
      (
        await api.get<PreviewIngreso>(`${decisiones(businessId)}/preview`, {
          params: {employee_id: employeeId, proyecto_id: proyectoId},
        })
      ).data,
    enabled: !!businessId && !!employeeId,
    gcTime: 0,
  })
}

export function useDecidirIngreso(businessId: string, proyectoId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      employeeId: string
      autorizado: boolean
      motivo: string | null
      decisionToken: string
    }) =>
      (
        await api.post<DecisionIngreso>(decisiones(businessId), {
          employee_id: input.employeeId,
          proyecto_id: proyectoId ?? null,
          autorizado: input.autorizado,
          autorizacion_motivo: input.motivo,
          decision_token: input.decisionToken,
        })
      ).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({queryKey: ['quien-entra-hoy', businessId, proyectoId]})
      queryClient.invalidateQueries({queryKey: ['ingreso-preview', businessId, variables.employeeId]})
      track('decision_ingreso_tomada', {business_id: businessId, autorizado: variables.autorizado})
    },
  })
}
